import { defineStore, acceptHMRUpdate } from 'pinia';
import get from 'lodash/get';
import { useOrganizationStore } from '@/stores/organization.store';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import { Themes } from '@pendo/services/BuildingBlocks';
import { AppsService } from '@/common/apps/apps.service';
import groupBy from 'lodash/groupBy';
import { fetchAllThemes, fetchOrganizationThemes, createTheme, deleteTheme } from '@/stores/api/themes.api';
import { computeGuidePlatformType } from '@/utils/apps';
import { canCreateGuide } from '@/guides/helpers/common.util';

import { useSubscriptionStore } from '@/stores/subscription.store';

let loadAllPromise: Promise<void> | null = null;

function getInitialState () {
    return {
        map: {} as Record<string, unknown>,
        areAllSubThemesLoaded: false,
    };
}

export const useThemesStore = defineStore('themes', {
    state: getInitialState,
    getters: {
        getThemeById: (state) => (id: string) => state.map[id],
        list: (state): unknown[] => values(state.map),
        listForAppId () {
            return (appId: unknown) => (this.list as Record<string, unknown>[]).filter((theme) => theme.appId === appId);
        },
        getDefaultThemeForAppId () {
            return (appId = -323232) => {
                const appThemes = this.listForAppId(appId);

                return (appThemes as Array<Record<string, string>>).find((theme) => theme.tags.toLowerCase().includes('default'));
            };
        },
        getOrgThemes (): Record<string, unknown>[] {
            return (this.list as Record<string, unknown>[]).filter((theme) => theme.orgSharedResourceSettings);
        },
        getOrgThemesForAppId () {
            return (appId: unknown) => {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const app = useAppsStore().getAppById(appId);

                if (!app) return [];

                const subscription = useSubscriptionStore().getActiveSub;

                if (!subscription) return [];

                const activeSubId = String(get(subscription, 'id', ''));
                const appPlatform = computeGuidePlatformType(app.platform);

                return this.getOrgThemes.filter((theme: Record<string, unknown>) => {
                    const { orgSharedResourceSettings } = theme || {};
                    const { platform, subList = [] } = (orgSharedResourceSettings as Record<string, unknown>) || {};

                    return appPlatform === platform && (subList as string[]).map(String).includes(activeSubId);
                });
            };
        },
    },
    actions: {
        setThemes ({ themes }: { themes: unknown[] }) {
            this.map = keyBy(themes, 'id') as Record<string, unknown>;
        },
        setTheme ({ theme }: { theme: Record<string, unknown> }) {
            this.map[theme.id as string] = theme;
        },
        removeThemeFromMap ({ id }: { id: string }) {
            delete this.map[id];
        },
        setAreAllSubThemesLoaded ({ loaded }: { loaded: boolean }) {
            this.areAllSubThemesLoaded = loaded;
        },
        async loadAll ({ reFetch = false } = {}) {
            if (loadAllPromise && !reFetch) {
                return loadAllPromise;
            }

            if (Object.keys(this.map).length && !reFetch) {
                return;
            }

            loadAllPromise = (async () => {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-require-imports
                    const { useAppsStore } = require('@/stores/apps.store');

                    const activeOrganization = useOrganizationStore().getActive;
                    const orgId = get(activeOrganization, 'id', null);
                    const activeSubscription = useSubscriptionStore().getActiveSub;
                    const subId = get(activeSubscription, 'id', null);
                    const includeOrganizationResources = get(
                        activeOrganization,
                        'organizationFlags.enableResourceSharing',
                        false,
                    );

                    const apps = useAppsStore().list;

                    const [appThemes, orgThemes] = await Promise.all([
                        fetchAllThemes() as Promise<Array<Record<string, unknown>>>,
                        orgId && includeOrganizationResources
                            ? (fetchOrganizationThemes(orgId, subId) as Promise<Array<Record<string, unknown>>>)
                            : Promise.resolve([] as Array<Record<string, unknown>>),
                    ]);

                    const themesByAppId = groupBy(appThemes, 'appId');

                    const appHasDefaultTheme = (appId: unknown) => {
                        const appThemesForId = themesByAppId[appId as string] || [];

                        return appThemesForId.some((theme) => (theme.tags as string).toLowerCase().includes('default'));
                    };

                    const guideApps = apps.filter(
                        (app: Record<string, unknown>) => app.integrated || AppsService.isLegacyApp(app),
                    );

                    const appsNeedingDefaultTheme = guideApps.filter(
                        ({ id }: { id: unknown }) => canCreateGuide(id) && !appHasDefaultTheme(id),
                    );

                    const createDefaultThemeForApp = ({ platform, id }: { platform: string; id: unknown }) => {
                        const defaultTheme = Themes.createTheme({
                            name: 'Default',
                            type: 'default',
                            platform: computeGuidePlatformType(platform),
                        });
                        defaultTheme!.appId = id;

                        return createTheme(defaultTheme);
                    };

                    const createdDefaultThemes = await Promise.all(appsNeedingDefaultTheme.map(createDefaultThemeForApp));

                    this.setThemes({ themes: [...appThemes, ...createdDefaultThemes, ...orgThemes] });
                    this.setAreAllSubThemesLoaded({ loaded: true });
                } finally {
                    loadAllPromise = null;
                }
            })();

            return loadAllPromise;
        },
        async createTheme ({ theme }: { theme: unknown }) {
            const savedTheme = await createTheme(theme) as Record<string, unknown>;
            this.setTheme({ theme: savedTheme });

            return savedTheme.id;
        },
        async deleteTheme ({ id }: { id: string }) {
            await deleteTheme(id);
            this.removeThemeFromMap({ id });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useThemesStore, module.hot));
}
